import React from 'react';
import { useSelector } from 'react-redux';
import { selectDrone } from '@src/features/fleet/pages/drones-page/redux';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import { ArcButton, ArcIconButton, ArcPagination, ArcSkeleton, ArcStack, ArcTable, ArcTableBody, ArcTableCell, ArcTableHead, ArcTableRow } from '@src/core/ui';
import { arcDateWithTime, arcDownloadFile, arcSecToMin, arcSpatium } from '@src/core/extensions';
import { PAGINATION_DEFAULT_SIZE, ZERO } from '@src/core/config';
import { DroneEntity } from '@src/core/features';
import { useFlightSummaryAllMutation, useFlightSummaryByIdMutation, useGetDroneOrderHistoryQuery } from '@src/features/fleet/redux/api';
import { useUnitType } from '@src/core/features/settings';
import { trackError, toTrackedError } from '@src/core/sentry';
import { toast } from 'react-toastify';

const OrderHistoryPanel: React.FC = () => {
  // Subscribe so the totalDistance column (arcSpatium) re-renders on a live unit
  // switch (UNITS Standard R1).
  useUnitType();
  const [page, setPage] = React.useState(0);
  const { id } = useSelector(selectDrone) ?? {} as DroneEntity;
  const { data = { data: [], totalCount: ZERO }, isLoading, isFetching } = useGetDroneOrderHistoryQuery({
    id,
    page,
    size: PAGINATION_DEFAULT_SIZE,
  });

  const [ flightSummaryAll, { isLoading: isDownloadingAll } ] = useFlightSummaryAllMutation();
  const [ flightSummaryById ] = useFlightSummaryByIdMutation();

  
  

  if (isLoading || isFetching) {
    return (
      <ArcStack spacing={1}>
        {Array(7).fill(1).map((_, idx) => {
          return <ArcSkeleton key={idx} height={40} />;
        })}
      </ArcStack>
    );
  }

  const onDownloadById = (id: number) => {
    flightSummaryById(id).unwrap().then((filePath) => {
      arcDownloadFile(filePath, `drone_order_${id}_history.csv`);
    }).catch((err: unknown) => {
      trackError(toTrackedError(err), { code: 'FE_0021', extra: { flow: 'flightSummaryById' } });
    });
  };

  const onDownloadAll = () => {
    if (isDownloadingAll) return;
    flightSummaryAll().unwrap().then((filePath) => {
      arcDownloadFile(filePath, `all_history.csv`);
    }).catch((err: unknown) => {
      trackError(toTrackedError(err), { code: 'FE_0021', extra: { flow: 'flightSummaryAll' } });
      toast.error('Failed to download flight summaries. Please try again.');
    });
  };

  
  return (
    <ArcStack sx={{ overflowY: 'scroll' }} maxHeight='60vh'>
      <ArcStack direction='row' justifyContent='flex-end' marginLeft='auto' marginBottom={2}>
        <ArcButton.brand onClick={onDownloadAll} isLoading={isDownloadingAll}>
          {isDownloadingAll ? 'Downloading...' : 'Download All'}
        </ArcButton.brand>
      </ArcStack>
      <ArcTable>
        <ArcTableHead>
          <ArcTableRow>
            <ArcTableCell> Date </ArcTableCell>
            <ArcTableCell> Flight Time </ArcTableCell>
            <ArcTableCell> Distance </ArcTableCell>
            <ArcTableCell> Order # </ArcTableCell>
            <ArcTableCell> Flight Data</ArcTableCell>
          </ArcTableRow>
        </ArcTableHead>

        <ArcTableBody>
          {data.data.map((order) => {
            return (
              <ArcTableRow key={order.id}>
                <ArcTableCell> {arcDateWithTime(order.createDate)} </ArcTableCell>
                <ArcTableCell> {arcSecToMin(order.totalFlightTime)} </ArcTableCell>
                <ArcTableCell> {arcSpatium(order.totalDistance, { fromSi: true })} </ArcTableCell>
                <ArcTableCell> {order.id} </ArcTableCell>
                <ArcTableCell> 
                  <ArcIconButton onClick={() => onDownloadById(order.id)}> 
                    <DownloadForOfflineIcon />  
                  </ArcIconButton> 
                </ArcTableCell>
              </ArcTableRow>
            );
          })}
        </ArcTableBody>
      </ArcTable>
      {PAGINATION_DEFAULT_SIZE < data.totalCount &&
        (
          <ArcStack mt={2} alignItems='center'>
            <ArcPagination 
              page={page + 1} 
              count={Math.ceil(data.totalCount / PAGINATION_DEFAULT_SIZE)} 
              onChange={(_, page) => setPage(page - 1)} />
          </ArcStack>
        )
      }
    </ArcStack>
  );
};

export default OrderHistoryPanel;
